import { REPAIR_COST_BP, REPAIR_MIN_MS, REPAIR_TIME_MS, STRUCTURES, WORKSHOP, type Resources } from '@somnhold/content';
import { idiv, mulBp } from '../math/fixed.ts';
import { addRes, baseCost, builtMaxHp, event, maxCharges, scaleRes, sortedCellKeys } from './core.ts';
import { standing } from './eras.ts';
import { perkBp } from './progress.ts';
import type { CampaignState, Ms, PlayerState } from './types.ts';

export interface RepairQuote {
  /** Missing share in bp: HP for buildings, charges for traps. */
  missingBp: number;
  cost: Resources;
  /** Work time of the job. */
  ms: Ms;
}

/** What repairing the structure on a cell costs and how long it takes for this player; null if nothing to mend. */
export const repairQuote = (s: CampaignState, key: string, p: PlayerState, now: Ms): RepairQuote | null => {
  const b = s.cells[key]?.built;
  if (!b) return null;
  const max = builtMaxHp(b, now);
  const mc = maxCharges(b);
  const missingBp = max > 0 ? Math.trunc(((max - b.hp) * 10000) / max) : mc > 0 ? Math.trunc(((mc - b.charges) * 10000) / mc) : 0;
  if (missingBp <= 0) return null;
  const cost = scaleRes(baseCost(s, b.structure), mulBp(Math.trunc((REPAIR_COST_BP * missingBp) / 10000), 10000 - perkBp(p.hero, 'repairDiscount')));
  // The golems (and any hero who helps) do the work, longer the worse the damage.
  let ms = mulBp(REPAIR_TIME_MS, missingBp);
  if (p.hero.heroClass === 'artificer') ms = idiv(ms, 2);
  // The artificer's workshop speeds every repair in the bastion.
  if (standing(s, 'workshop')) ms = mulBp(ms, WORKSHOP.repairTimeBp);
  if (ms < REPAIR_MIN_MS) ms = REPAIR_MIN_MS;
  return { missingBp, cost, ms };
};

/** Repair order for the whole bastion: gates, then walls, then towers, then the rest; worst first within each. */
export const repairPriority = (structure: string): number => {
  if (structure === 'gate') return 0;
  if (structure === 'wall' || structure === 'rampart') return 1;
  if (STRUCTURES[structure as keyof typeof STRUCTURES]?.category === 'tower') return 2;
  return 3;
};

/**
 * The Order sends masons (set by an admin, to help a group through a hard stretch): every structure, ruins
 * included, back to full health and charges. A repair already ordered is not needed any more: what was paid
 * into it goes back to the storage. Returns how many structures were mended.
 */
export const rebuildAll = (s: CampaignState, now: Ms): number => {
  let n = 0;
  for (const key of sortedCellKeys(s)) {
    const c = s.cells[key];
    const b = c?.built;
    if (!c || !b) continue;
    const max = builtMaxHp(b, now);
    const mc = maxCharges(b);
    if (b.hp < max || b.charges < mc) {
      b.hp = max;
      b.charges = mc;
      n++;
    }
    if (c.job?.kind === 'repair') {
      addRes(s.storage, c.job.paid);
      c.job = null;
    }
  }
  event(s, now, 'rebuilt', { data: { n } });
  s.version++;
  return n;
};
